/**
 * Product category options, served by the backend.
 *
 * `Product#category` is format-scoped: the legal set depends on the product's
 * `form`, so `Salads` is valid on a buffet dish and invalid on a Meal Box one.
 * The vocabulary lives in `Product::Categories` (app/models/product.rb) and
 * reaches the frontend as the `productCategories` prop on `AppRouter`, set by
 * `app/views/products/{index,edit}.rb`.
 *
 * There is deliberately no hardcoded list here. Create, edit and the list
 * filter all read this one server-supplied source, which is the whole point of
 * the ticket — a hand-maintained frontend copy is what let create and edit
 * drift apart.
 */
export interface ProductCategoryOptions {
  /** Category values keyed by Product::Forms value. */
  by_form: Record<string, string[]>;
  /** Union across every format — for surfaces with no single format in scope. */
  all: string[];
}

// Frozen (including the nested collections — Object.freeze is shallow): this
// is a shared module-level default handed to every route that doesn't have
// its own product data yet, so an accidental mutation at one call site would
// otherwise leak into every other consumer. The cast is safe: freezing adds
// no fields, it only stops writes the interface never promised to allow.
export const EMPTY_CATEGORY_OPTIONS = Object.freeze({
  by_form: Object.freeze({}),
  all: Object.freeze([]),
}) as unknown as ProductCategoryOptions;

/**
 * Returns the category values valid for a given form key.
 * Mirrors Product::Categories.for_form(form) on the backend — the single source
 * for both the dropdown options and the form-change clear logic.
 * @param options - the server-supplied option set
 * @param form - Product::Forms key (e.g. "buffet", "meal_box")
 * @returns the form's categories, or [] for an unknown/blank form
 */
export function categoriesForForm(
  options: ProductCategoryOptions | undefined,
  form: string | undefined,
): string[] {
  if (!options || !form) return [];
  return options.by_form[form] ?? [];
}
